<script setup lang="ts">
import { Icon } from '@iconify/vue2'
import { onClickOutside } from '@vueuse/core'
import { computed, ref, toRefs, watch } from 'vue'
import Checkbox from '../../../../components/ui/checkbox/Checkbox.vue'
import DropdownColunas from './DropdownColunas.vue'
import { SortOption } from './dtos/SortOption.dto'
import { SortDirection } from './enums/SortDirection.enum'
import { TableColumn } from './dtos/TableColumn.dto'
import TableHeaderCell from './TableHeaderCell.vue'

const emit = defineEmits<{
	(event: 'sort', value: SortOption): void
	(event: 'update:checked', value?: string[]): void
}>()

const props = withDefaults(
	defineProps<{
		data: any[]
		colunas: TableColumn[]
		displayMode?: boolean
		checked?: string[]
	}>(),
	{
		displayMode: false,
	},
)

watch(
	() => props.checked,
	(value) => {
		emit('update:checked', value)
	},
)

const { data } = toRefs(props)

const checkAll = ref(false)

watch(checkAll, (value) => {
	if (value) {
		emit(
			'update:checked',
			data.value.map((dado) => dado.id),
		)
	} else {
		emit('update:checked', [])
	}
})

const allColumnsKeys = computed(() => {
	return props.colunas.map((d) => d.key)
})

const columnsEnabled = ref(allColumnsKeys.value)

// columnsEnabled nasce com as colunas do mount; uma coluna que o pai passe a enviar
// depois (ex.: coluna opcional ativada pelo usuário) ficaria filtrada para sempre.
watch(allColumnsKeys, (keys, oldKeys) => {
	const addedKeys = keys.filter(
		(key) => !oldKeys.includes(key) && !columnsEnabled.value.includes(key),
	)
	if (addedKeys.length) columnsEnabled.value = [...columnsEnabled.value, ...addedKeys]
})

function handleSort(key: string, direction?: SortDirection) {
	emit('sort', { key, direction })
}

const dropdownColunas = ref()
const openColunas = ref(false)

onClickOutside(dropdownColunas, () => {
	openColunas.value = false
})
</script>

<template>
	<table class="w-full">
		<thead class="w-full">
			<tr class="text-bold text-[#01395E] w-full">
				<th
					v-if="!displayMode"
					class="w-[2%] p-1 bg-blue-tint-3 text-start"
				>
					<Checkbox v-model="checkAll" />
				</th>
				<th
					v-for="coluna in colunas.filter((f) => columnsEnabled.includes(f.key))"
					:key="coluna.key"
					class="p-1 bg-blue-tint-3 text-start"
				>
					<table-header-cell
						:table-header="coluna"
						@directionChange="handleSort(coluna.sortKey || coluna.key, $event)"
					/>
				</th>
				<th class="p-1 bg-blue-tint-3 text-start relative">
					<div
						@click="openColunas = true"
						v-if="false"
					>
						<Icon
							icon="charm:menu-kebab"
							class="cursor-pointer"
						/>
					</div>
					<div class="absolute z-10 -translate-x-full -translate-y-1\5">
						<DropdownColunas
							v-if="openColunas"
							ref="dropdownColunas"
							v-model="columnsEnabled"
							class="min-w-max"
							:options="
								colunas.map((d) => {
									return { label: d.name, value: d.key }
								})
							"
						/>
					</div>
				</th>
			</tr>
		</thead>
		<tbody class="border-2 border-gray-light">
			<tr
				v-for="dado in data"
				:key="dado.id + checked?.length"
				class="even:bg-[#F1F5F8]"
				:class="checked?.includes(dado.id) ? '!bg-blue-tint-4' : ''"
			>
				<td
					v-if="!displayMode"
					class="p-1 border-b-2 border-b-gray-light"
				>
					<Checkbox
						:value="checked"
						:model-value="dado.id"
						@input="emit('update:checked', $event)"
					/>
				</td>

				<td
					v-for="coluna in colunas.filter((f) => columnsEnabled.includes(f.key))"
					:key="coluna.key"
					:title="dado[coluna.key]"
					class="border-b-2 border-b-gray-light max-w-xs"
				>
					<slot
						:name="coluna.key"
						v-bind="dado"
					>
						<div class="text-ellipsis overflow-hidden whitespace-nowrap">
							<div
								v-if="coluna.component"
								:key="dado.id"
							>
								<Component
									:is="coluna.component"
									v-bind="{ ...dado, text: dado[coluna.key] }"
								/>
							</div>
							<span v-else> {{ dado[coluna.key] }}</span>
						</div>
					</slot>
				</td>
				<td class="border-b-2 border-b-gray-light">
					<slot
						v-if="!displayMode"
						name="custom-edit"
						v-bind="dado"
					>
					</slot>
				</td>
			</tr>
		</tbody>
	</table>
</template>

<style scoped>
table thead tr th:nth-child(1) {
	border-top-left-radius: 5px;
}

table thead tr th:last-child {
	border-top-right-radius: 5px;
}

table tbody tr td {
	@apply py-1;
}
</style>
